import { useEffect, useMemo, useState } from "react";
import { Search, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { inr, fieldCls } from "@/components/admin/orderUtils";

export const ItemCostsAdmin = () => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [err, setErr] = useState(false);

  useEffect(() => { api.get("/admin/item-costs").then(({ data }) => setRows(data)).catch(() => setErr(true)); }, []);

  const shown = useMemo(() => (rows || []).filter((r) =>
    r.name.toLowerCase().includes(q.toLowerCase()) && (!onlyMissing || r.cost_price == null)), [rows, q, onlyMissing]);

  if (err) return <p className="text-center py-16 text-charcoal/60" data-testid="item-costs-load-error">Could not load items. Please refresh.</p>;
  if (!rows) return <div className="h-64 rounded-3xl bg-white/60 border border-warm animate-pulse" />;
  const missing = rows.filter((r) => r.cost_price == null).length;

  return (
    <div className="space-y-4" data-testid="item-costs-admin">
      <p className="text-sm text-charcoal/60">Enter the vendor cost and your selling price for each product or experience. Only admins can see cost prices; they are used to calculate profit.</p>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items" className={`${fieldCls} w-full pl-9`} data-testid="item-costs-search" />
        </div>
        <label className="flex items-center gap-2 text-sm text-charcoal/70">
          <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} data-testid="item-costs-missing-toggle" className="accent-rose-600" />
          Only missing cost ({missing})
        </label>
      </div>
      <div className="space-y-2">
        {shown.map((r) => <CostRow key={r.item_id} row={r} onSaved={(upd) => setRows((all) => all.map((x) => (x.item_id === r.item_id ? { ...x, ...upd } : x)))} />)}
        {!shown.length && <p className="text-center py-10 text-charcoal/50" data-testid="item-costs-empty">No items found.</p>}
      </div>
    </div>
  );
};

const CostRow = ({ row, onSaved }) => {
  const [sell, setSell] = useState(String(row.selling_price ?? ""));
  const [cost, setCost] = useState(row.cost_price == null ? "" : String(row.cost_price));
  const [busy, setBusy] = useState(false);
  const dirty = sell !== String(row.selling_price ?? "") || cost !== (row.cost_price == null ? "" : String(row.cost_price));
  const margin = cost !== "" && Number(sell) > 0 ? Math.round(((Number(sell) - Number(cost)) / Number(sell)) * 100) : null;

  const save = async () => {
    setBusy(true);
    const payload = { kind: row.kind, selling_price: sell === "" ? null : Number(sell), cost_price: cost === "" ? null : Number(cost) };
    try {
      await api.put(`/admin/item-costs/${row.item_id}`, payload);
      onSaved({ selling_price: payload.selling_price ?? row.selling_price, cost_price: payload.cost_price });
      toast.success(`Saved ${row.name.trim()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-warm p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`item-cost-row-${row.item_id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-charcoal truncate">{row.name}</p>
        <p className="text-xs text-charcoal/50 capitalize">{row.kind} · {row.category}{margin != null && <span className={margin < 0 ? "text-rose-600" : "text-emerald-600"}> · margin {margin}% ({inr(Number(sell) - Number(cost))})</span>}</p>
      </div>
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <label className="text-[10px] uppercase tracking-wider font-semibold text-charcoal/50">Selling ₹
          <input type="number" min="0" value={sell} onChange={(e) => setSell(e.target.value)} className={`${fieldCls} w-full sm:w-28`} data-testid={`item-sell-input-${row.item_id}`} />
        </label>
        <label className="text-[10px] uppercase tracking-wider font-semibold text-charcoal/50">Cost ₹
          <input type="number" min="0" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="not set" className={`${fieldCls} w-full sm:w-28`} data-testid={`item-cost-input-${row.item_id}`} />
        </label>
        <button onClick={save} disabled={!dirty || busy} data-testid={`item-cost-save-${row.item_id}`}
          className="h-[38px] w-10 rounded-xl rose-glow text-white flex items-center justify-center disabled:opacity-30 transition-opacity">
          <Save className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
